import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { vehicleService, teamService, vehicleTeamService } from '../services/api';
import { useDepartement } from '../context/DepartementContext';
import Loader from '../components/Loader';
import './VehiclesPage.css';

// ── Add Team Modal ──────────────────────────────────────────
function AddTeamModal({ departements, selectedDepartement, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    departementId: selectedDepartement?.id || '',
    entryHour: '',
    exitHour: '',
    status: 1,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const backdropRef         = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())        { setError('Team name is required.');    return; }
    if (!form.departementId)      { setError('Department is required.');   return; }
    setSaving(true); setError('');
    try {
      await teamService.create({
        name:          form.name.trim(),
        departementId: form.departementId,
        entryHour:     form.entryHour || null,
        exitHour:      form.exitHour  || null,
        status:        1,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create team.');
    } finally { setSaving(false); }
  };

  return createPortal(
    <div
      className="avt-backdrop"
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
    >
      <div className="avt-modal">
        <div className="avt-header">
          <div className="avt-header-left">
            <i className="fas fa-users avt-header-icon"></i>
            <div>
              <h3 className="avt-title">Add Team</h3>
              <p className="avt-subtitle">Create a new team and link it to a department</p>
            </div>
          </div>
          <button className="avt-close" onClick={onClose} type="button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form className="avt-body" onSubmit={handleSubmit}>
          {error && (
            <div className="avt-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          <div className="avt-field">
            <label className="avt-label">Team Name <span className="avt-required">*</span></label>
            <input
              className="avt-input"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. KITCHEN"
              autoFocus
            />
          </div>

          <div className="avt-field">
            <label className="avt-label">Department <span className="avt-required">*</span></label>
            {selectedDepartement ? (
              <input
                className="avt-input avt-input--readonly"
                value={selectedDepartement.name}
                readOnly
              />
            ) : (
              <select
                className="avt-select"
                name="departementId"
                value={form.departementId}
                onChange={handleChange}
              >
                <option value="">— Select a department —</option>
                {departements.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="avt-row">
            <div className="avt-field">
              <label className="avt-label">Entry Hour</label>
              <input
                className="avt-input"
                type="time"
                name="entryHour"
                value={form.entryHour}
                onChange={handleChange}
              />
            </div>
            <div className="avt-field">
              <label className="avt-label">Exit Hour</label>
              <input
                className="avt-input"
                type="time"
                name="exitHour"
                value={form.exitHour}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="avt-footer">
            <button type="button" className="avt-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="avt-btn-save" disabled={saving}>
              {saving
                ? <><i className="fas fa-spinner fa-spin"></i> Saving…</>
                : <><i className="fas fa-plus"></i> Create Team</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Assign Vehicle Modal ────────────────────────────────────
function AssignVehicleModal({ team, allVehicles, assignedVehicleIds, onClose, onSuccess }) {
  const [vehicleId, setVehicleId] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const backdropRef               = useRef(null);

  // Only vehicles not already assigned to any team
  const available = allVehicles.filter((v) => !assignedVehicleIds.has(v.id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vehicleId) { setError('Please select a vehicle.'); return; }
    setSaving(true); setError('');
    try {
      await vehicleTeamService.create({ teamId: team.id, vehicleId, status: 1 });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign vehicle.');
    } finally { setSaving(false); }
  };

  return createPortal(
    <div
      className="avt-backdrop"
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
    >
      <div className="avt-modal">
        <div className="avt-header">
          <div className="avt-header-left">
            <i className="fas fa-shuttle-van avt-header-icon"></i>
            <div>
              <h3 className="avt-title">Assign Vehicle to {team.name}</h3>
              <p className="avt-subtitle">Only unassigned vehicles are shown</p>
            </div>
          </div>
          <button className="avt-close" onClick={onClose} type="button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form className="avt-body" onSubmit={handleSubmit}>
          {error && (
            <div className="avt-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          <div className="avt-field">
            <label className="avt-label">Vehicle <span className="avt-required">*</span></label>
            <select
              className="avt-select"
              value={vehicleId}
              onChange={(e) => { setVehicleId(e.target.value); setError(''); }}
            >
              <option value="">— Select a vehicle —</option>
              {available.length === 0 && (
                <option disabled>All vehicles are already assigned</option>
              )}
              {available.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>

          <div className="avt-footer">
            <button type="button" className="avt-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="avt-btn-save" disabled={saving || available.length === 0}>
              {saving
                ? <><i className="fas fa-spinner fa-spin"></i> Saving…</>
                : <><i className="fas fa-plus"></i> Assign</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Team Card ──────────────────────────────────────────────
function TeamCard({ team, vehicleTeams, allVehicles, assignedVehicleIds, onAssigned, onRemoved }) {
  const [open, setOpen]             = useState(false);
  const [showModal, setShowModal]   = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const handleRemove = async (vtId) => {
    setRemovingId(vtId);
    try {
      await vehicleTeamService.delete(vtId);
      onRemoved();
    } catch {
      // silently ignore; parent will refresh anyway
    } finally { setRemovingId(null); }
  };

  return (
    <>
      <div className="team-card">
        <div className="team-card-header" onClick={() => setOpen((o) => !o)}>
          <div className="team-card-left">
            <i className="fas fa-users team-card-icon"></i>
            <span className="team-card-name">{team.name}</span>
            <span className="team-card-badge">{vehicleTeams.length} vehicle{vehicleTeams.length !== 1 ? 's' : ''}</span>
            {team.entryHour && (
              <span className="team-card-hours">
                <i className="fas fa-clock"></i> {team.entryHour} – {team.exitHour}
              </span>
            )}
          </div>
          <div className="team-card-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="team-card-add-btn"
              title="Assign vehicle"
              onClick={() => setShowModal(true)}
            >
              <i className="fas fa-plus"></i> Assign
            </button>
            <i className={`fas fa-chevron-right team-card-chevron${open ? ' open' : ''}`}
               onClick={() => setOpen((o) => !o)}></i>
          </div>
        </div>

        {open && (
          <div className="team-card-body">
            <table className="vehicles-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vehicle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {vehicleTeams.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="vt-no-results">No vehicles assigned to this team.</td>
                  </tr>
                ) : (
                  vehicleTeams.map((vt, idx) => (
                    <tr key={vt.id}>
                      <td className="vt-index">{idx + 1}</td>
                      <td className="vt-reg">{vt.vehicleName}</td>
                      <td className="vt-actions">
                        <button
                          className="vt-remove-btn"
                          title="Remove from team"
                          onClick={() => handleRemove(vt.id)}
                          disabled={removingId === vt.id}
                        >
                          {removingId === vt.id
                            ? <i className="fas fa-spinner fa-spin"></i>
                            : <i className="fas fa-times"></i>}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <AssignVehicleModal
          team={team}
          allVehicles={allVehicles}
          assignedVehicleIds={assignedVehicleIds}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); onAssigned(); }}
        />
      )}
    </>
  );
}

// ── Page ───────────────────────────────────────────────────
function VehiclesPage() {
  const { departements, selectedDepartement } = useDepartement();

  const [allVehicles, setAllVehicles]     = useState([]);
  const [teams, setTeams]                 = useState([]);
  const [vehicleTeams, setVehicleTeams]   = useState([]);   // all assignments
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [search, setSearch]               = useState('');
  const [teamFilter, setTeamFilter]       = useState('');
  const [showAddTeam, setShowAddTeam]     = useState(false);
  const [toast, setToast]                 = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = selectedDepartement ? { departementId: selectedDepartement.id } : {};
      const [vRes, tRes, vtRes] = await Promise.all([
        vehicleService.getAll(),
        teamService.getAll(params),
        vehicleTeamService.getAll(),
      ]);
      setAllVehicles(vRes.data?.result || []);
      setTeams(tRes.data?.result || []);
      setVehicleTeams(vtRes.data?.result || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data.');
    } finally { setLoading(false); }
  }, [selectedDepartement]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Set of vehicleIds already assigned to any team
  const assignedVehicleIds = new Set(vehicleTeams.map((vt) => vt.vehicleId));

  // vehicleTeams grouped by teamId
  const vtByTeam = vehicleTeams.reduce((acc, vt) => {
    if (!acc[vt.teamId]) acc[vt.teamId] = [];
    acc[vt.teamId].push(vt);
    return acc;
  }, {});

  // Filter teams by search/teamFilter
  const filteredTeams = teams.filter((t) => {
    const nameMatch = t.name.toLowerCase().includes(search.toLowerCase());
    const filterMatch = teamFilter ? t.id === teamFilter : true;
    return nameMatch && filterMatch;
  });

  const handleReset = () => { setSearch(''); setTeamFilter(''); };

  const handleTeamAdded = () => {
    setShowAddTeam(false);
    fetchAll();
    showToast('Team created successfully.');
  };

  const handleAssigned = () => {
    fetchAll();
    showToast('Vehicle assigned successfully.');
  };

  const handleRemoved = () => {
    fetchAll();
    showToast('Vehicle removed from team.');
  };

  const titleSlot = document.getElementById('page-title-slot');

  if (loading) return <Loader size="lg" />;

  if (error) {
    return (
      <div className="vehicles-state-box vehicles-error">
        <i className="fas fa-exclamation-triangle"></i>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <>
      {titleSlot && createPortal(
        <h1 className="vehicles-page-title">
          {selectedDepartement ? `${selectedDepartement.name} — Vehicles` : 'Vehicles'}
        </h1>,
        titleSlot
      )}

      {toast && (
        <div className="vehicles-toast">
          <i className="fas fa-check-circle"></i> {toast}
        </div>
      )}

      {/* Top bar: filter + Add Team button */}
      <div className="vf-bar">
        <div className="vf-field">
          <i className="fas fa-search vf-icon"></i>
          <input
            type="text"
            className="vf-input"
            placeholder="Search team..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="vf-field">
          <i className="fas fa-layer-group vf-icon"></i>
          <select
            className="vf-select"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <option value="">All teams</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {(search || teamFilter) && (
          <button className="vf-reset" onClick={handleReset}>
            <i className="fas fa-times"></i> Clear
          </button>
        )}
        <span className="vf-count">{filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''}</span>

        <button
          className="vf-add-team-btn"
          onClick={() => setShowAddTeam(true)}
        >
          <i className="fas fa-plus"></i> Add Team
        </button>
      </div>

      {/* Team cards */}
      {teams.length === 0 ? (
        <div className="vehicles-state-box vehicles-empty">
          <i className="fas fa-users"></i>
          <span>No teams yet. Click "Add Team" to create one.</span>
        </div>
      ) : (
        <div className="team-cards-list">
          {filteredTeams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              vehicleTeams={vtByTeam[team.id] || []}
              allVehicles={allVehicles}
              assignedVehicleIds={assignedVehicleIds}
              onAssigned={handleAssigned}
              onRemoved={handleRemoved}
            />
          ))}
          {filteredTeams.length === 0 && (
            <div className="vehicles-state-box vehicles-empty">
              <i className="fas fa-users"></i>
              <span>No teams match your filters.</span>
            </div>
          )}
        </div>
      )}

      {showAddTeam && (
        <AddTeamModal
          departements={departements}
          selectedDepartement={selectedDepartement}
          onClose={() => setShowAddTeam(false)}
          onSaved={handleTeamAdded}
        />
      )}
    </>
  );
}

export default VehiclesPage;
